/**
 * The organization's cameras, read from one shared list (see devicesAtoms).
 *
 * A list reads `useCameras()`. Whatever shows one camera reads `cameraAtom(id)` and
 * `cameraStatusAtom(id)` from apiReads, so a status frame re-renders only what shows that
 * camera. A loop or a callback that looks cameras up by id reads `cameraIndexAtom` from
 * apiReads.
 */

import { useAtomValueRawSync, useSetAtom } from 'jotai';

import {
    camerasAtom, devicesAtom, recordPendingAddAtom, recordPendingRenameAtom,
    reloadCamerasAtom,
} from '@/state/api/devicesAtoms';
import type { FetchState } from '@/types/common';
import type { FlatDevice } from '@/types/devices';

interface Cameras extends FetchState {
    /** The same list from one reload to the next; a status frame never changes it. */
    cameras: FlatDevice[];
    /** False until the first list arrives, true from then on. */
    isLoaded: boolean;
}

export function useCameras(): Cameras {
    const { isLoading, isLoaded, error } = useAtomValueRawSync(devicesAtom);
    const cameras = useAtomValueRawSync(camerasAtom);
    const refetch = useSetAtom(reloadCamerasAtom);
    return { cameras, isLoaded, isLoading, error, refetch };
}

export function useRecordPendingAdd(): (device: FlatDevice) => void {
    return useSetAtom(recordPendingAddAtom);
}

export function useRecordPendingRename(): (id: string, name: string) => void {
    return useSetAtom(recordPendingRenameAtom);
}
